// Where the user came from, so a detail page's "Back to X" link can point at the
// list they actually used rather than a hardcoded one (Task 24795).
//
// Task 24770 gave every detail page a "← Back to X" link, but the destination is
// a constant on each page: /single-job/59378 always said "Back to Jobs List"
// even when you had opened it from a customer's Jobs tab. The note on that prop
// is still right, and is why this is not simply history.go(-1):
//
//   "A ROUTE, never history.go(-1): you arrive at a detail page from a search
//    result, an email link or the address bar as often as from its own list."
//
// So instead of guessing, list pages SAY who they are, and a detail page uses
// that only when the list is genuinely the page it came from. Everything else
// falls back to the hardcoded route, which is still correct for a link pasted
// into a fresh tab.
//
// sessionStorage rather than localStorage on purpose: this is per-tab context.
// Two tabs open on two different lists must not answer for each other, and none
// of it should survive a browser restart.
//
// Every access is wrapped: sessionStorage throws in some privacy modes, and a
// back link is never worth breaking a page over.

const LIST_ORIGIN_KEY = "crmit_list_origin";
const PREVIOUS_PATH_KEY = "crmit_previous_path";

const readJson = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (_) {
    /* ignore storage errors - the link falls back to its hardcoded route */
  }
};

// A list page announcing itself: `to` is where to go back to (path + query),
// `label` is what the link should read.
export const rememberListOrigin = (origin) => {
  if (!origin?.to || !origin?.label) return;
  writeJson(LIST_ORIGIN_KEY, { to: origin.to, label: origin.label });
};

export const readListOrigin = () => {
  const origin = readJson(LIST_ORIGIN_KEY);
  return origin?.to && origin?.label ? origin : null;
};

// The route we were on BEFORE the current one.
//
// It has to be the previous route rather than "whatever rendered last", because
// every page is lazy(): on a cold chunk the router renders the Suspense
// fallback, AuthProtected's effect fires, and only then does the real page
// render. A "last path" would already have been overwritten with the detail
// page's own URL by the time that page got to ask. This value is written once
// per navigation and then holds still, so it reads the same whether the page
// rendered immediately or three hundred milliseconds later.
export const rememberPreviousPath = (path) => {
  if (!path || typeof path !== "string" || !path.startsWith("/")) return;
  try {
    sessionStorage.setItem(PREVIOUS_PATH_KEY, path);
  } catch (_) {
    /* ignore storage errors */
  }
};

export const readPreviousPath = () => {
  try {
    return sessionStorage.getItem(PREVIOUS_PATH_KEY) || null;
  } catch (_) {
    return null;
  }
};

// Compare two routes by path only. A list carries its query string so returning
// to it keeps whatever the URL held, but "is this where I came from?" must not
// turn on a query string that changed while the user was on the page.
export const samePath = (a, b) => {
  if (!a || !b) return false;
  const strip = (value) => String(value).split("?")[0].split("#")[0];
  return strip(a) === strip(b);
};
